import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE } from '../../config';

/**
 * P-010 c15 — Patient Consent Dashboard.
 * Patient-facing view of which AI agents / EHRs have consent to call which tools against the
 * patient's data (the append-only MCP consent ledger), the patient's own tool-call volume, and a
 * one-click revoke (append-only revoke event + <=60s propagation). Reads through the app backend
 * proxy (/api/mcp-consent/*), which forwards to the MCP's own consent routes — the MCP owns its
 * isolated governance DB; the app never touches it.
 */
function AIAccessConsent({ fetchWithAuth }) {
  const [grants, setGrants] = useState([]);
  const [usage, setUsage] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [revoking, setRevoking] = useState(null); // key of the grant being revoked

  const grantKey = (g) => `${g.requester}|${g.tool}|${g.action}`;

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [gRes, uRes] = await Promise.all([
        fetchWithAuth(`${API_BASE}/api/mcp-consent/grants`),
        fetchWithAuth(`${API_BASE}/api/mcp-consent/usage`),
      ]);
      if (!gRes.ok || !uRes.ok) throw new Error('Unable to load your AI access settings.');
      const gData = await gRes.json();
      const uData = await uRes.json();
      setGrants(gData.grants || []);
      setUsage(uData.usage || {});
    } catch (e) {
      setError(e.message || 'Unable to load your AI access settings.');
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth]);

  useEffect(() => { load(); }, [load]);

  const revoke = async (g) => {
    setRevoking(grantKey(g));
    setError('');
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/mcp-consent/revoke`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          requester: g.requester, tool: g.tool, action: g.action,
          context: g.context || 'patient',
        }),
      });
      if (!res.ok) throw new Error('Revoke failed. Please try again.');
      await load(); // refresh — the revoked grant should disappear
    } catch (e) {
      setError(e.message || 'Revoke failed. Please try again.');
    } finally {
      setRevoking(null);
    }
  };

  return (
    <div className="ns-container">
      <div className="card ns-card">
        <div className="card-header">
          <h3 className="card-title">Connected Data Access</h3>
        </div>
        <div className="ns-cardBody">
          <p style={{ color: 'var(--brand-text-muted, #5a6b82)', marginTop: 0 }}>
            These are the AI agents and connected health systems you have authorized to access your
            Healist data through the Model Context Protocol. Every access is consent-gated and
            immutably audited. Revoking takes effect within 60 seconds.
          </p>

          {loading && <p>Loading your AI access settings…</p>}
          {!loading && error && <p className="form-error" style={{ color: '#b42318' }}>{error}</p>}

          {!loading && !error && grants.length === 0 && (
            <div className="push-status-box push-status-box--inactive" style={{ marginTop: 12 }}>
              No external partners or agents currently have access to your data. When you authorize a
              partner, agent, or connected provider, it will appear here for you to review or revoke.
            </div>
          )}

          {!loading && !error && grants.length > 0 && (
            <div className="consent-grant-list" style={{ marginTop: 12 }}>
              {grants.map((g) => (
                <div key={grantKey(g)} className="card" style={{ marginBottom: 10, padding: '14px 16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ fontWeight: 600 }}>{g.requester}</div>
                      <div style={{ fontSize: 13, color: 'var(--brand-text-muted, #5a6b82)' }}>
                        Tool: <code>{g.tool}</code> · Action: {g.action}
                        {g.consent_version ? ` · Consent ${g.consent_version}` : ''}
                      </div>
                    </div>
                    <button
                      className="btn btn-secondary"
                      disabled={revoking === grantKey(g)}
                      onClick={() => revoke(g)}
                    >
                      {revoking === grantKey(g) ? 'Revoking…' : 'Revoke'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="card ns-card">
        <div className="card-header">
          <h3 className="card-title">Connected Access Activity</h3>
        </div>
        <div className="ns-cardBody">
          {loading && <p>Loading…</p>}
          {!loading && Object.keys(usage).length === 0 && (
            <p style={{ color: 'var(--brand-text-muted, #5a6b82)', margin: 0 }}>
              No AI tool calls have been made against your data yet.
            </p>
          )}
          {!loading && Object.keys(usage).length > 0 && (
            <table className="consent-usage-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>
                  <th style={{ padding: '6px 8px' }}>Tool</th>
                  <th style={{ padding: '6px 8px' }}>Allowed</th>
                  <th style={{ padding: '6px 8px' }}>Denied</th>
                  <th style={{ padding: '6px 8px' }}>Errors</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(usage).map(([tool, counts]) => (
                  <tr key={tool} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '6px 8px' }}><code>{tool}</code></td>
                    <td style={{ padding: '6px 8px' }}>{counts.allow || 0}</td>
                    <td style={{ padding: '6px 8px' }}>{counts.deny || 0}</td>
                    <td style={{ padding: '6px 8px' }}>{counts.error || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default AIAccessConsent;
